import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import MovieCard from "@/components/feature/MovieCard";
import { allMovies } from "@/mocks/movies";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || "1", 10);
  const movie = allMovies.find((m) => m.id === movieId);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">电影未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该电影资源可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-block bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold px-6 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = allMovies
    .filter(
      (m) =>
        m.id !== movie.id &&
        (m.categorySlug === movie.categorySlug || m.director === movie.director)
    )
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <nav
              className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap"
              aria-label="面包屑导航"
            >
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <Link
                to={`/category/${movie.categorySlug}`}
                className="hover:text-primary-600 transition-colors duration-200 cursor-pointer"
              >
                {movie.category}
              </Link>
              <span>/</span>
              <span className="text-foreground-700 font-medium">{movie.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="md:w-72 lg:w-80 shrink-0">
                <div className="rounded-lg overflow-hidden bg-background-200 aspect-[2/3] sticky top-24">
                  <img
                    src={movie.posterUrl}
                    alt={movie.title}
                    title={`${movie.title} - 天堂影视高清蓝光在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <div className="flex items-center gap-1 bg-primary-500/10 rounded-lg px-3 py-1">
                    <i className="ri-star-fill text-primary-500 text-sm"></i>
                    <span className="text-sm font-semibold text-primary-600">{movie.rating}</span>
                  </div>
                  <span className="text-sm text-foreground-400">{movie.year}</span>
                  <span className="text-foreground-300">·</span>
                  <Link
                    to={`/category/${movie.categorySlug}`}
                    className="text-sm text-primary-600 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
                  >
                    {movie.category}
                  </Link>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-400">{movie.duration}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-5 rounded-lg bg-background-100">
                  <div>
                    <span className="text-xs text-foreground-400">导演</span>
                    <p className="text-sm text-foreground-800 mt-0.5">{movie.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">主演</span>
                    <p className="text-sm text-foreground-800 mt-0.5">{movie.actors.join(" / ")}</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="text-base font-heading font-semibold text-foreground-900 mb-3">
                    剧情简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold px-6 py-3 rounded-lg text-sm transition-all duration-200 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                    <i className="ri-play-circle-line text-base"></i>
                    立即播放
                  </button>
                  <button className="flex-1 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium px-6 py-3 rounded-lg text-sm transition-all duration-200 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                    <i className="ri-heart-line text-base"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <div className="mt-14">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} {...m} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${movie.title} - 天堂影视高清蓝光在线观看`,
            description: movie.description,
            url: `https://tiantangmovies.com/article/${movie.id}`,
            image: movie.posterUrl,
            author: {
              "@type": "Organization",
              name: "天堂影视",
            },
            publisher: {
              "@type": "Organization",
              name: "天堂影视",
            },
            datePublished: `${movie.year}-01-01`,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://tiantangmovies.com/article/${movie.id}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://tiantangmovies.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: movie.category,
                item: `https://tiantangmovies.com/category/${movie.categorySlug}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: movie.title,
                item: `https://tiantangmovies.com/article/${movie.id}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}